import 'twin.macro';
import 'styled-components/macro';
import ReactMarkdown, { Options as ReactMarkdownProps } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';

import CodeBlock from 'component-lib/atoms/code-block/CodeBlock';
import { ErrorBoundary } from 'support/ErrorBoundary';
import { defaultComponents } from 'support/markdown';

import { Lightbox } from '../lightbox/Lightbox';

export interface MarkdownProps {
  allowedElements?: string[];
  className?: string;
  children?: string;
  unwrapDisallowed?: boolean;
}

const components: ReactMarkdownProps['components'] = {
  ...defaultComponents,
  code: ({ node, inline, ...props }) => {
    if (inline) {
      return <code {...props} />;
    }

    const [language, nocopy] = props.className?.split('language-')[1]?.split(',nocopy') || [];

    // This check is correct, we want exactly '', !nocopy would break it
    const disableCopy = nocopy === '';

    return (
      <CodeBlock
        codeTagProps={{
          style: {
            fontFamily: undefined,
            fontSize: undefined,
          },
        }}
        customStyle={{
          background: undefined,
          fontFamily: undefined,
          fontSize: undefined,
          lineHeight: undefined,
          margin: undefined,
          padding: undefined,
        }}
        language={language}
        disableCopy={disableCopy}
        PreTag="div"
      >
        {String(props.children).replace(/\n$/, '')}
      </CodeBlock>
    );
  },
  img: ({ node, ...props }) => {
    const { src, alt } = props;

    if (!src) {
      return null;
    }

    return <Lightbox src={src} alt={alt} {...props} />;
  },
  table: ({ node, ...props }) => (
    <div tw="border border-[#5a6478] my-3 overflow-x-auto rounded">
      <table tw="my-0!" {...props} />
    </div>
  ),
  ol: ({ node, depth, ordered, ...props }) => <ol type={depth % 2 ? 'a' : '1'} {...props} />,
};

export const Markdown = ({ allowedElements, children = '', className, unwrapDisallowed }: MarkdownProps) => {
  return (
    // Wrapping in an error boundary incase of an HTML parsing issue
    <ErrorBoundary fallbackComponent={() => <MarkdownUnwrapped className={className}>{children}</MarkdownUnwrapped>}>
      <MarkdownUnwrapped
        allowedElements={allowedElements}
        className={className}
        rehypePlugins={[rehypeRaw]}
        unwrapDisallowed={unwrapDisallowed}
        components={components}
      >
        {children}
      </MarkdownUnwrapped>
    </ErrorBoundary>
  );
};

const MarkdownUnwrapped = (props: ReactMarkdownProps) => {
  return (
    <ReactMarkdown
      tw="overflow-hidden prose max-w-none"
      remarkPlugins={[gfm]}
      components={props.components}
      {...props}
    />
  );
};
